import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    useTheme,
    useMediaQuery,
    TablePagination,
    TextField,
    InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { getDetailedQueueReport } from "../../api/api";
import { getCurrentUser } from "../../utils/auth";


const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
        backgroundColor: theme.palette.action.selected,
    },
}));

const StatusChip = ({ status }) => {
    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case "WAITING":
                return "warning";
            case "SERVING":
                return "primary";
            case "ASSISTED":
                return "success";
            case "LOST":
                return "error";
            case "DONE":
                return "info";
            case "CANCELLED":
                return "error";
            default:
                return "default";
        }
    };

    return (
        <Chip
            label={status || "Unknown"}
            color={getStatusColor(status)}
            size="small"
            variant="outlined"
        />
    );
};

const Detailed_Queue_Report = ({ startDate, endDate }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const user = getCurrentUser();
                setCurrentUser(user);

                const startDateParam = startDate ? startDate.format("YYYY-MM-DD") : undefined;
                const endDateParam = endDate ? endDate.format("YYYY-MM-DD") : undefined;
                const response = await getDetailedQueueReport(startDateParam, endDateParam);
                
                if (response.success) {
                    setData(response.data);
                    setFilteredData(response.data);
                } else {
                    setError(response.message || "Failed to fetch data");
                }
            } catch (err) {
                console.error("Error fetching detailed queue report:", err);
                setError("Failed to load detailed queue report");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    // Filter data based on search term
    useEffect(() => {
        if (!searchTerm) {
            setFilteredData(data);
        } else {
            const filtered = data.filter((row) =>
                Object.values(row).some((value) =>
                    String(value).toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
            setFilteredData(filtered);
        }
        setPage(0); // Reset to first page when filtering
    }, [searchTerm, data]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return "N/A";
        return new Date(dateTime).toLocaleString();
    };

    const formatTime = (time) => {
        if (!time) return "N/A";
        return new Date(time).toLocaleTimeString();
    };

    const formatWaitTime = (waitTimeMinutes) => {
        if (!waitTimeMinutes || waitTimeMinutes === 0) return "N/A";
        return `${waitTimeMinutes} min`;
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Detailed Queue Report
                    {currentUser?.branch_name && ` - ${currentUser.branch_name}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Total Records: {filteredData.length}
                </Typography>
                
                {/* Search Field */}
                <TextField
                    placeholder="Search queue data..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{ minWidth: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size={isMobile ? "small" : "medium"}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Queue ID
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Queue Number
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Service
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Status
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Note
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Queue At Arrival
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Registration Time
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Service Time
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Wait Time
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Phone Number
                            </TableCell>
                            <TableCell sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'primary.contrastText', 
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}>
                                Registration Type
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                                <StyledTableRow key={row.queue_id || index}>
                                    <TableCell>{row.queue_id}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {row.queue_number}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{row.service}</TableCell>
                                    <TableCell>
                                        <StatusChip status={row.status} />
                                    </TableCell>
                                    <TableCell>{row.note || "N/A"}</TableCell>
                                    <TableCell>{row.queue_at_arrival || "N/A"}</TableCell>
                                    <TableCell>{formatDateTime(row.registration_time)}</TableCell>
                                    <TableCell>{formatTime(row.service_time)}</TableCell>
                                    <TableCell>{formatWaitTime(row.wait_time_minutes)}</TableCell>
                                    <TableCell>
                                        {row.phone_number ? (
                                            <Box>
                                                <Typography variant="body2">
                                                    {row.phone_number}
                                                </Typography>
                                                {row.is_sms_registration && (
                                                    <Chip
                                                        label="SMS"
                                                        color="primary"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        ) : (
                                            "N/A"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.registration_type || "N/A"}
                                            color={row.is_sms_registration ? "primary" : "default"}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default Detailed_Queue_Report;
